import React from "react";
import {App as AntdApp, Card, Space, Switch, Tag, Typography, Affix} from "antd";

import OrgTree from "../components/OrgTree.jsx";
import OrgNodeDetails from "../components/OrgNodeDetails.jsx";
import OrgTaxonomyPanel from "../components/OrgTaxonomyPanel.jsx";
import OrgProductFieldsEditor from "../components/OrgProductFieldsEditor.jsx";

import {useOrgTree} from "../hooks/useOrgTree.js";
import {createNode, deleteNode as apiDelete, loadGlobalAiModel, loadOrgTree, renameNode as apiRename, updateCooperativaAiModel} from "../api/orgApi.js";
import {listFieldRegistry} from "@modules/fields/api/fieldRegistryApi.js";
import {normalizeFieldKey} from "@modules/fields/utils/fieldRegistry.js";

import {useSSE} from "@modules/realtime/hooks/useSSE.js";

import "../assets/css/styles.css";
import {getTypeBadge} from "@modules/org/components/typeBadge.js";

const {Title, Text} = Typography;

export default function OrgTreePage()
{
	const {notification, message, modal} = AntdApp.useApp();

	const {
		nodes,
		asTreeData,
		findNode,
		addChild,
		renameNode,
		deleteNode,
		allowedChildren,
		setFromServer,
		setAllowedProductFields,
		setAiModel
	} = useOrgTree();

	React.useEffect(() =>
	{
		(async () =>
		{
			const items = await loadOrgTree();
			if (items?.length)
				setFromServer(items);
		})();
	}, [setFromServer]);

	useSSE("org", {
		"org.node.created": async (payload) =>
		{
			notification.success({
				message: "Nuovo nodo creato",
				description: `${payload.type} “${payload.name}”`,
				placement: "topRight"
			});

			const items = await loadOrgTree();
			if (items?.length)
			{
				setFromServer(items);
				const parentKey = payload.parentId ? String(payload.parentId) : "root";
				setExpandedKeys(keys => Array.from(new Set([...keys, parentKey])));
				// setSelectedKey(String(payload.id));
				setHighlightKey(String(payload.id));
			}
		},

		"org.node.renamed": async (payload) =>
		{
			notification.success({
				message: "Nodo rinominato",
				description: `${payload.type} “${payload.name}”`,
				placement: "topRight"
			});

			const items = await loadOrgTree();
			if (items?.length)
				setFromServer(items);
		},

		"org.node.productFields.updated": async () =>
		{
			const items = await loadOrgTree();
			if (items?.length)
				setFromServer(items);
		},

		"org.node.aiModel.updated": async () =>
		{
			const items = await loadOrgTree();
			if (items?.length)
				setFromServer(items);
		},

		"org.node.deleted": async (payload) =>
		{
			notification.success({
				message: "Nodo eliminato",
				description: "Il nodo con i suoi rami sono stati eliminati",
				placement: "topRight"
			});

			const items = await loadOrgTree();
			if (items?.length)
				setFromServer(items);

			setSelectedKey(prev =>
			{
				if (!prev) return prev;
				if (String(prev) === String(payload?.id)) return "root";
				return prev;
			});
		}
	});

	const [filter, setFilter] = React.useState("");
	const [highlightKey, setHighlightKey] = React.useState(null);
	const [selectedKey, setSelectedKey] = React.useState(null);
	const [expandedKeys, setExpandedKeys] = React.useState(["root"]);
	const [showExtras, setShowExtras] = React.useState(() =>
	{
		try
		{
			return localStorage.getItem("org.showExtras") === "true";
		}
		catch (e)
		{
			return false;
		}
	});
	const [productFieldOptions, setProductFieldOptions] = React.useState([]);
	const [productFieldsLoading, setProductFieldsLoading] = React.useState(true);

	const [top] = React.useState(65);

	// Modello AI globale: mostrato come valore ereditato quando una cooperativa non ha override.
	const [globalAiModel, setGlobalAiModel] = React.useState("");

	React.useEffect(() =>
	{
		let active = true;

		(async () =>
		{
			const model = await loadGlobalAiModel();
			if (active)
				setGlobalAiModel(model);
		})();

		return () =>
		{
			active = false;
		};
	}, []);


	React.useEffect(() =>
	{
		try
		{
			localStorage.setItem("org.showExtras", showExtras);
		}
		catch (e)
		{
			console.error("org -> localStorage.setItem error", e);
		}
	}, [showExtras]);

	React.useEffect(() =>
	{
		let active = true;

		(async () =>
		{
			try
			{
				setProductFieldsLoading(true);
				const result = await listFieldRegistry();
				if (!active)
					return;

				if (result.ok !== false)
					setProductFieldOptions(Array.isArray(result.fields) ? result.fields.filter((f) => !f?.bannerOnly) : []);
				else
					setProductFieldOptions([]);
			}
			catch (error)
			{
				if (!active)
					return;

				setProductFieldOptions([]);
				notification.error({
					message: "Caricamento campi prodotto",
					description: error?.response?.data?.error || error?.message || "Impossibile recuperare la configurazione",
					placement: "bottomRight"
				});
			}
			finally
			{
				if (active)
					setProductFieldsLoading(false);
			}
		})();

		return () =>
		{
			active = false;
		};
	}, [notification]);


	const selectedInfo = React.useMemo(() =>
	{
		if (!selectedKey) return null;
		return findNode(nodes, selectedKey);
	}, [nodes, selectedKey, findNode]);

	const fieldRegistryMap = React.useMemo(() =>
	{
		const byKey = new Map();
		const byName = new Map();
		for (const field of productFieldOptions)
		{
			if (field?.key)
				byKey.set(field.key, field);
			if (field?.name)
				byName.set(field.name.toLowerCase(), field);
		}
		return {byKey, byName};
	}, [productFieldOptions]);

	// null = catalogo campi non disponibile: il pannello tassonomie mostra allora tutte le schede.
	const registryFieldKeys = React.useMemo(
		() => (productFieldOptions.length ? new Set(productFieldOptions.map((field) => field?.key).filter(Boolean)) : null),
		[productFieldOptions]
	);

	const normalizedAllowedFields = React.useMemo(() =>
	{
		const allowed = selectedInfo?.node?.allowedProductFields || [];
		return allowed.map((field) =>
		{
			const key = normalizeFieldKey(field?.key || field?.name);
			const byKey = fieldRegistryMap.byKey.get(key);
			const byName = fieldRegistryMap.byName.get(String(field?.name || "").toLowerCase());
			const source = byKey || byName;
			if (source)
			{
				return {
					key: source.key,
					name: source.name,
					type: source.type,
					dateMode: source.dateMode
				};
			}
			return {
				key,
				name: field?.name || key,
				type: field?.type
			};
		}).filter((field) => field?.key);
	}, [selectedInfo?.node?.allowedProductFields, fieldRegistryMap]);

	const canAddChild = React.useMemo(() =>
	{
		if (!selectedInfo) return false;
		const next = allowedChildren(selectedInfo.node.type);
		return next.length > 0;
	}, [selectedInfo, allowedChildren]);

	const isRoot = React.useMemo(() =>
	{
		if (!selectedInfo) return false;
		return selectedInfo.node.type === "ROOT";
	}, [selectedInfo]);

	const onAdd = async (name) =>
	{
		if (!selectedKey)
			return;

		// UI locale immediata
		const childKey = addChild(selectedKey, name);
		if (!childKey)
			return;

		setExpandedKeys(keys =>
		{
			if (keys.includes(selectedKey))
				return keys;
			return [...keys, selectedKey];
		});
		// setSelectedKey(childKey);
		setHighlightKey(childKey)

		// POST al server — ROOT o non-ROOT
		const sel = findNode(nodes, selectedKey)?.node;
		if (sel?.serverId)
		{
			const body = sel.type === "ROOT"
				? {parentKey: "root", name}
				: {parentId: sel.serverId, name};

			const res = await createNode(body);
			if (!res?.ok)
			{
				notification.error({
					message: "Creazione server fallita",
					description: res?.error || "Verifica API / proxy",
					placement: "bottomRight"
				});
			}
			// L’altro browser (e questo) si allineano via SSE + refetch
		}
	};

	const onRename = async (name) =>
	{
		if (!selectedKey)
			return;

		// update locale
		renameNode(selectedKey, name);

		const sel = findNode(nodes, selectedKey)?.node;
		if (sel?.serverId)
		{
			const res = await apiRename(sel.serverId, name);
			if (!res?.ok)
			{
				notification.error({
					message: "Rinomina server fallita",
					description: res?.error || "Verifica API / proxy",
					placement: "bottomRight"
				});
			}
		}
	};

	const onDelete = async () =>
	{
		if (!selectedKey)
			return;

		const sel = findNode(nodes, selectedKey)?.node;

		const okLocal = deleteNode(selectedKey);
		if (okLocal) setSelectedKey("root");

		if (sel?.serverId)
		{
			const res = await apiDelete(sel.serverId);
			if (!res?.ok)
			{
				notification.error({
					message: "Eliminazione server fallita",
					description: res?.error || "Verifica API / proxy",
					placement: "bottomRight"
				});
			}
		}
	};


	const onSaveAiModel = async (model) =>
	{
		const sel = selectedInfo?.node;
		if (!sel?.serverId)
			return false;

		const res = await updateCooperativaAiModel(sel.serverId, model);
		if (!res?.ok)
		{
			notification.error({
				message: "Salvataggio modello AI fallito",
				description: res?.error || "Verifica API / proxy",
				placement: "bottomRight"
			});
			return false;
		}

		// Allineamento locale immediato; gli altri client seguono via SSE.
		setAiModel(sel.key, res.aiModel || "");

		message.success(res.aiModel
			? `Modello AI impostato su “${res.aiModel}”`
			: "Modello AI ripristinato al valore globale");

		return true;
	};

	const isCooperativa = selectedInfo?.node?.type === "COOPERATIVA";
	const cooperativaId = isCooperativa ? selectedInfo?.node?.serverId : null;
	const cooperativaName = isCooperativa ? selectedInfo?.node?.name : "";

	const b = getTypeBadge(selectedInfo?.node?.type);

	return (
		<div className="grid grid-2 org-tree-layout">
			<Affix offsetTop={top}>
				<Card className="card-elev">
					<div className="org-bar">
						<Title level={5} style={{marginBottom: 0}}>
							Albero organizzazione
						</Title>

						<Space>
							<Text type="secondary">Più Info</Text>
							<Switch checked={showExtras} onChange={setShowExtras}/>
						</Space>
					</div>

					<OrgTree
						treeData={asTreeData}
						onSelect={setSelectedKey}
						filter={filter}
						showExtras={showExtras}
						expandedKeys={expandedKeys}
						onExpandedChange={setExpandedKeys}
						selectedKey={selectedKey}
						highlightKey={highlightKey}
					/>
				</Card>
			</Affix>

			<div className="org-right-column">
				<Card className="card-elev" style={{height: "fit-content"}}>
					<Title level={5} style={{marginBottom: 8}}>Dettagli</Title>
					<OrgNodeDetails
						selected={selectedKey}
						path={selectedInfo?.path || []}
						onAdd={onAdd}
						onRename={onRename}
						onDelete={onDelete}
						canAddChild={canAddChild}
						isRoot={isRoot}
						type={selectedInfo?.node?.type}
						aiModel={selectedInfo?.node?.aiModel || ""}
						globalAiModel={globalAiModel}
						onSaveAiModel={onSaveAiModel}
					/>
				</Card>

				{isCooperativa ? (
					<Card className="card-elev" style={{marginTop: 4}}>
						<Title level={5} style={{marginBottom: 8}}>Campi prodotto consentiti</Title>
						<OrgProductFieldsEditor
							cooperativaId={cooperativaId}
							cooperativaName={cooperativaName}
							availableFields={productFieldOptions}
							allowedFields={normalizedAllowedFields}
							loadingOptions={productFieldsLoading}
							onLocalUpdate={(fields) =>
							{
								if (selectedKey)
									setAllowedProductFields(selectedKey, fields);
							}}
						/>
					</Card>
				) : null}

				<Card className="card-elev org-taxonomy-card">
					<Title level={5} style={{marginBottom: 8}}>Risorse</Title>
					{
						isCooperativa
							? (
								<div className="path">
									{(selectedInfo?.path || []).map((n, i) =>
										<span key={n.key} className="crumb">
											{i > 0 ? " / " : ""}{n.name}
										</span>
									)}
									<Tag color={b.color} style={{...b.style, marginLeft: 10}}>
										{b.label}
									</Tag>
								</div>
							)
							: (<Text type="secondary">Seleziona una cooperativa per visualizzare le risorse collegate.</Text>)
					}
					{
						isCooperativa ? (
							<OrgTaxonomyPanel
								cooperativaId={cooperativaId}
								cooperativaName={cooperativaName}
								registryFieldKeys={registryFieldKeys}
								registryLoading={productFieldsLoading}
							/>
						) : ''
					}
				</Card>
			</div>
		</div>
	);
}
